import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { deepEqual } from "@tellescope/utils";
import { useCallback, useMemo } from "react";
import { useAppPermissionQuery } from "../permissions";
import { ALL_INBOX_VIEW_ID, useInboxViewShallowStore } from "../store";
import { DEFAULT_INBOX_FILTERS, type InboxFilters } from "../pages/messaging/utils/inbox-filters";
import { TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";

export const INBOX_TABLE_VIEW_PAGE = `${TELLESCOPE_2_TABLE_VIEW_PREFIX}Inbox` as const;

export interface UseInboxTableViewsResult {
  views: TellescopeModelFromName<"table_views">[];
  selectedView: TellescopeModelFromName<"table_views"> | undefined;
  viewId: string;
  setViewId: (id: string) => void;
  isAllView: boolean;
  isLoadingViews: boolean;
  effectiveFilters: InboxFilters;
  setStagedFilters: (filters: InboxFilters) => void;
  applyFilters: (filters: InboxFilters) => void;
  resetStagedFilters: () => void;
  createView: (title: string, filters: InboxFilters) => Promise<string>;
  deleteSelectedView: () => Promise<void>;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  hasUnsavedChanges: boolean;
}

const parseFiltersFromView = (view: TellescopeModelFromName<"table_views"> | undefined): InboxFilters =>
  view?.filter ? (view.filter as InboxFilters) : DEFAULT_INBOX_FILTERS;

const isEmptyFilterValue = (value: unknown): boolean =>
  value === undefined ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" && value !== null && Object.keys(value).length === 0);

const omitEmptyFilters = (filters: InboxFilters): InboxFilters =>
  Object.entries(filters).reduce<InboxFilters>(
    (acc, [key, value]) => (isEmptyFilterValue(value) ? acc : { ...acc, [key]: value }),
    {}
  );

/**
 * Saved-view controller for the inbox thread list: which view is selected, the filters in effect, and
 * creating/updating/deleting views.
 *
 * Views are `table_views` records scoped to the {@link INBOX_TABLE_VIEW_PAGE} page key, stored with
 * `columns: []` because the inbox is not a table — when the thread row becomes configurable those
 * same records gain a populated `columns` array.
 */
export const useInboxTableViews = (): UseInboxTableViewsResult => {
  const { currentUser } = useAuth();
  const userId = currentUser.id;

  const { viewId, setViewId, defaultViewFiltersByUserId, setDefaultViewFilters, clearDefaultViewFilters } =
    useInboxViewShallowStore(
      "viewId",
      "setViewId",
      "defaultViewFiltersByUserId",
      "setDefaultViewFilters",
      "clearDefaultViewFilters"
    );

  const viewsQuery = useTellescopeInfiniteQuery({
    model: "table_views",
    method: "getSome",
    request: {
      limit: 500,
      filter: { page: INBOX_TABLE_VIEW_PAGE },
    },
    select: (data) => data.pages.flat(),
  });

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
    delete: { access: canDelete },
  } = useAppPermissionQuery({
    model: "table_views",
    actions: ["create", "update", "delete"],
    recordId: viewId !== ALL_INBOX_VIEW_ID ? viewId : undefined,
  });

  const createMutation = useTellescopeMutation({ model: "table_views", method: "createOne" });
  const updateMutation = useTellescopeMutation({ model: "table_views", method: "updateOne" });
  const deleteMutation = useTellescopeMutation({ model: "table_views", method: "deleteOne" });

  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);
  const selectedView = useMemo(() => views.find((view) => view.id === viewId), [views, viewId]);
  // An unknown id (a deleted view, or a stale link) falls back to the default view.
  const isAllView = viewId === ALL_INBOX_VIEW_ID || !selectedView;

  const persistedFilters = useMemo(() => parseFiltersFromView(selectedView), [selectedView]);
  const stagedDefaultFilters = defaultViewFiltersByUserId[userId];

  const effectiveFilters = isAllView ? stagedDefaultFilters ?? DEFAULT_INBOX_FILTERS : persistedFilters;

  const setStagedFilters = useCallback(
    (filters: InboxFilters) => setDefaultViewFilters(userId, filters),
    [setDefaultViewFilters, userId]
  );

  const resetStagedFilters = useCallback(() => clearDefaultViewFilters(userId), [clearDefaultViewFilters, userId]);

  const applyFilters = useCallback(
    (filters: InboxFilters) => {
      if (isAllView || !selectedView || !canUpdate) {
        setStagedFilters(filters);
        return;
      }

      updateMutation.mutate({
        id: selectedView.id,
        filter: filters,
        // Without this a filter the user cleared would linger on the record.
        _options: { replaceObjectFields: true },
      });
    },
    [isAllView, selectedView, canUpdate, setStagedFilters, updateMutation]
  );

  const createView = useCallback(
    async (title: string, filters: InboxFilters): Promise<string> => {
      const created = await createMutation.mutateAsync({
        page: INBOX_TABLE_VIEW_PAGE,
        title,
        columns: [],
        filter: filters,
      });
      resetStagedFilters();
      return created.id;
    },
    [createMutation, resetStagedFilters]
  );

  const deleteSelectedView = useCallback(async () => {
    if (!selectedView) return;
    await deleteMutation.mutateAsync(selectedView.id);
    setViewId(ALL_INBOX_VIEW_ID);
  }, [deleteMutation, selectedView, setViewId]);

  // Compared against the default after dropping empty keys: filters staged before cleared keys were
  // removed at the source still sit in localStorage, and a key holding `undefined` is not a change.
  const hasUnsavedChanges =
    isAllView &&
    stagedDefaultFilters !== undefined &&
    !deepEqual(omitEmptyFilters(stagedDefaultFilters), DEFAULT_INBOX_FILTERS, { includeRemovals: true });

  return {
    views,
    selectedView,
    viewId,
    setViewId,
    isAllView,
    isLoadingViews: viewsQuery.isLoading,
    effectiveFilters,
    setStagedFilters,
    applyFilters,
    resetStagedFilters,
    createView,
    deleteSelectedView,
    canCreate,
    canUpdate,
    canDelete,
    hasUnsavedChanges,
  };
};
